import type { Observable } from 'rxjs'
import { InputShellControl, InputShellRoot } from './InputShell'
import {
  useCallback,
  useRef,
  useState,
  type ChangeEventHandler,
  type Ref,
} from 'react'
import { useIsSkeleton } from '../Skeleton'
import { composeRefs } from '@radix-ui/react-compose-refs'
import styles from './TextArea.module.css'
import { mergeProps, useFocus } from 'react-aria'
import { useObservable } from '@/hooks/rx-hooks'

export interface TextAreaProps {
  /**
   * Number of visible text rows.
   */
  rows?: number
  /**
   * The current value of the textarea.
   */
  value?: string
  /**
   * Textarea placeholder.
   */
  placeholder?: string
  /**
   * When an item is emitted, focus is triggered on the textarea.
   * If `true`, prevents scrolling.
   */
  focus$?: Observable<boolean>
  /**
   * Optional ref.
   */
  ref?: Ref<HTMLTextAreaElement>
  /**
   * Whether the textarea is in an invalid state.
   */
  invalid?: boolean
  /**
   * Whether the textarea is disabled.
   */
  disabled?: boolean
  /**
   * Whether a skeleton overlay should be rendered.
   * Also disables the textarea.
   */
  isSkeleton?: boolean
  /**
   * Whether the textarea is focused.
   */
  isFocused?: boolean
  /**
   * Focus change handler.
   */
  onFocusChange?: (isFocused: boolean) => unknown
  /**
   * Change handler.
   */
  onChange?: ChangeEventHandler<HTMLTextAreaElement>
  /**
   * ARIA label for the textarea.
   */
  'aria-label'?: string
  /**
   * ARIA labelledby for the textarea.
   */
  'aria-labelledby'?: string
  /**
   * Whether the text area should receive focus on render.
   */
  autoFocus?: boolean
  /**
   * Test ID attribute.
   */
  testId?: string
}

export function TextArea({
  ref,
  invalid,
  disabled,
  isSkeleton,
  focus$,
  rows,
  placeholder,
  onFocusChange,
  testId,
  ...rest
}: TextAreaProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  disabled ||= isSkeleton

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [isFocused, setFocused] = useState(false)
  const focusChange = useCallback(
    (isFocused: boolean) => {
      setFocused(isFocused)
      onFocusChange?.(isFocused)
    },
    [onFocusChange],
  )
  const { focusProps } = useFocus({
    isDisabled: disabled,
    onFocusChange: focusChange,
  })

  // Trigger focus when observable emits
  useObservable(focus$, (preventScroll: boolean) => {
    inputRef.current?.focus({ preventScroll })
  })

  return (
    <InputShellRoot
      isTextField
      invalid={invalid}
      isDisabled={disabled}
      isFocused={isFocused}
      isSkeleton={isSkeleton}
    >
      <InputShellControl>
        <textarea
          ref={composeRefs(ref, inputRef)}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          data-test={testId}
          {...mergeProps(focusProps, rest)}
          className={styles.textarea}
        />
      </InputShellControl>
    </InputShellRoot>
  )
}
